import {
  EventCategory,
  PhysicianNotifiedEventType,
  type ComplianceResponse,
  type DefaultPatientAlertResponse,
  type DefaultPatientEventResponse,
} from "@/api";
import { getFullMeasurement } from "@/features/patient/profile/compliance-summary";
import { getServerNow } from "@/utils/server-time";

/**
 * Formatter for calendar dates (YYYY-MM-DD) in an IANA timezone. Shared so the
 * "is this today?" comparisons all read dates the same way `todayInTimezone`
 * writes them — `en-CA` is what makes the format ISO.
 */
export function dayFormatter(timezone: string): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat("en-CA", { timeZone: timezone });
}

/** Today's date as YYYY-MM-DD in the given IANA timezone. */
export function todayInTimezone(timezone: string): string {
  return dayFormatter(timezone).format(getServerNow());
}

/** A patient completed today iff today's full-measurement day has completed >= expected (expected > 0). */
export function isCompletedToday(
  compliance: ComplianceResponse,
  todayISO: string,
): boolean {
  const fm = getFullMeasurement(compliance);
  if (!fm) return false;
  const day = fm.daily_counts.find((d) => d.date === todayISO);
  if (!day) return false;
  return day.expected > 0 && day.completed >= day.expected;
}

/** Resolved iff there is a "regular" treating-physician notification dated today. */
export function isResolvedToday(
  events: DefaultPatientEventResponse[],
  todayISO: string,
): boolean {
  return events.some(
    (e) =>
      e.event_category === EventCategory.physician_notified &&
      e.event_category_type === PhysicianNotifiedEventType.regular &&
      e.event_date.slice(0, 10) === todayISO,
  );
}

export function unseenAlertCount(
  alerts: DefaultPatientAlertResponse[],
): number {
  return alerts.filter((a) => !a.alert_seen).length;
}
